/** @jsxImportSource @emotion/react */

import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { observer } from 'mobx-react-lite'
import { useMobxStore } from 'src/state'
import { useLocation } from 'react-router-dom'
import { Details } from './Details'
import { Subscription } from './Subscription'
import { Billing } from './Billing'
import { ConnectedAccount } from './ConnectedAccount'
import LoadingSpinner from 'src/components/LoadingSpinner'

interface NavButtonProps {
  isFocused: boolean
  label: string
  onClick: () => void
}

const NavButton = ({ isFocused, label, onClick }: NavButtonProps) => (
  <button
    onClick={onClick}
    css={css`
      height: 40px;
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      margin-right: 24px;
    `}
  >
    <p
      css={css`
        color: ${isFocused ? colors.WHITE : colors.ATTENDANCE_GRAY};
        font-family: 'Rift', sans-serif;
        font-size: 16px;
        font-weight: 500;
        text-transform: uppercase;
        padding-top: 8px;
      `}
    >
      {label}
    </p>
    <div
      css={css`
        opacity: ${isFocused ? 1 : 0};
        height: 3px;
        width: 24px;
        background-color: ${colors.DEFAULT_FLAIR};
        align-self: center;
      `}
    />
  </button>
)

export const GeneralSettings = observer(() => {
  const {
    me: {
      state: { account },
      fetchMe,
    },
    payment: {
      fetchPaymentList
    }
  } = useMobxStore()
  const [currentPage, setCurrentPage] = useState(0)
  const { search } = useLocation()

  const params = useMemo(() => {
    const regex = /\?origin=([^&]+)/
    const match = regex.exec(search)

    if (match) {
      const origin = match[1]

      return {
        origin,
      }
    }
  }, [search])

  useLayoutEffect(() => {
    fetchMe()
    fetchPaymentList()
  }, [])

  useLayoutEffect(() => {
    if (params?.origin === 'onboarding') {
      setCurrentPage(3)
    }
  }, [params])

  const renderPage = useCallback(() => {
    switch (currentPage) {
      case 0:
        return <Details />
      case 1:
        return <Subscription />
      case 2:
        return <Billing />
      case 3:
        return <ConnectedAccount />
      default:
        return <div />
    }
  }, [currentPage])

  if (!account?.full_name) {
    return (
      <div
        css={css`
          background-color: ${colors.HEADER};
          padding: 16px;
          border-radius: 8px;
          border: 1px solid ${colors.SOFT_STEEL};
          max-width: 820px;
          margin: 0 auto;
          margin-top: 50px;
        `}
      >
        <LoadingSpinner size={6} />
      </div>
    )
  }

  return (
    <main
      css={css`
        margin-top: 50px;
        padding-bottom: 50px;
      `}
    >
      <div
        css={css`
          background-color: ${colors.HEADER};
          padding: 16px;
          max-width: 820px;
          margin: 0 auto;
          gap: 16px;
          border-radius: 8px 8px 0 0;
          border: 1px solid ${colors.SOFT_STEEL};
        `}
      >
        <h1
          css={css`
            color: ${colors.WHITE};
            font-family: 'Rift', sans-serif;
            font-size: 40px;
            font-weight: 500;
          `}
        >
          ACCOUNT SETTINGS
        </h1>
      </div>
      <div
        css={css`
          background-color: ${colors.HEADER_BG_2};
          max-width: 820px;
          margin: 0 auto;
          height: 40px;
          border-right: 1px solid ${colors.SOFT_STEEL};
          border-bottom: 1px solid ${colors.SOFT_STEEL};
          border-left: 1px solid ${colors.SOFT_STEEL};
          padding-left: 16px;
          padding-right: 16px;
          border-radius: 0 0 8px 8px;
          display: flex;
          align-items: center;
          flex-shrink: 0;
        `}
      >
        <NavButton
          label='Details'
          onClick={() => setCurrentPage(0)}
          isFocused={currentPage === 0}
        />
        <NavButton
          label='Subscription'
          onClick={() => setCurrentPage(1)}
          isFocused={currentPage === 1}
        />
        {account?.subscription && (
          <>
            <NavButton
              label='Billing'
              onClick={() => setCurrentPage(2)}
              isFocused={currentPage === 2}
            />
            <NavButton
              label='Connected Account'
              onClick={() => setCurrentPage(3)}
              isFocused={currentPage === 3}
            />
          </>
        )}
      </div>
      <div>{renderPage()}</div>
    </main>
  )
})
